import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";
import type { User } from "@/app/types";

/* ============================================================================
 * LogRocket – JavaScript Web SDK (v12) integration for the customer portal
 * ----------------------------------------------------------------------------
 * - `initLogRocket()` is browser-only and a no-op while
 *   `NEXT_PUBLIC_LOGROCKET_APP_ID` is missing, so this module is safe to import
 *   from any bundle (SSR/build included) and stays inert until the key exists.
 * - Privacy first: typed input values, sensitive headers/keys, invitation and
 *   reset tokens in URLs, and IP capture are all disabled or redacted.
 * - Redux action/state logging is attached in `app/store/index.ts` through
 *   `logRocketReduxMiddleware()`.
 * ========================================================================== */

const APP_ID = process.env.NEXT_PUBLIC_LOGROCKET_APP_ID;
const RELEASE = process.env.NEXT_PUBLIC_APP_RELEASE;
const ENABLE_IN_DEV = process.env.NEXT_PUBLIC_LOGROCKET_ENABLE_DEV === "true";

/** Is an app id configured at all? (No key = integration dormant, never breaks) */
export const isLogRocketConfigured = Boolean(APP_ID);

/** Record in production only, unless explicitly enabled for local testing. */
export const isLogRocketEnabled =
  isLogRocketConfigured &&
  (process.env.NODE_ENV === "production" || ENABLE_IN_DEV);

const REDACTED = "[redacted]";
const MAX_DEPTH = 6;
const MAX_ARRAY_ITEMS = 50;
const MAX_BODY_LENGTH = 20_000;

/** Object keys that must never reach LogRocket. */
const SENSITIVE_KEY_PATTERN =
  /pass|pwd|otp|token|secret|auth|cookie|credential|card|cvv|ssn/i;

const SENSITIVE_HEADERS = new Set([
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-auth-token",
  "x-api-key",
  "x-csrf-token",
]);

/** Query params that must never be recorded (invitation / reset links). */
const SENSITIVE_QUERY_PARAMS = [
  "token",
  "otp",
  "code",
  "resetToken",
  "invitationToken",
  "apikey",
  "apiKey",
  "key",
  "signature",
];

let initialised = false;

/**
 * Deep-redacts sensitive keys and caps depth/array size so large Redux states
 * stay cheap to record.
 */
export function scrubSensitiveData(value: any, depth = 0): any {
  if (value === null || value === undefined) return value;
  if (depth >= MAX_DEPTH) return "[truncated]";

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => scrubSensitiveData(item, depth + 1));
    if (value.length > MAX_ARRAY_ITEMS) {
      items.push(`[+${value.length - MAX_ARRAY_ITEMS} more]`);
    }
    return items;
  }

  if (typeof value === "object") {
    if (value instanceof Date) return value.toISOString();
    if (value instanceof Error) {
      return { name: value.name, message: value.message };
    }
    const scrubbed: Record<string, any> = {};
    Object.entries(value).forEach(([key, val]) => {
      scrubbed[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? REDACTED
        : scrubSensitiveData(val, depth + 1);
    });
    return scrubbed;
  }

  return value;
}

/** Redacts token-like query params (e.g. `/auth/setup-password?token=...`). */
export function sanitizeUrl(url: string): string {
  try {
    const base =
      typeof window !== "undefined" ? window.location.origin : undefined;
    const parsed = new URL(url, base);
    let changed = false;

    SENSITIVE_QUERY_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED);
        changed = true;
      }
    });

    return changed ? parsed.toString() : url;
  } catch {
    return url;
  }
}

function sanitizeHeaders(headers: Record<string, any> = {}) {
  const out: Record<string, any> = {};
  Object.entries(headers).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    out[key] =
      SENSITIVE_HEADERS.has(lowerKey) || SENSITIVE_KEY_PATTERN.test(lowerKey)
        ? REDACTED
        : value;
  });
  return out;
}

/**
 * API bodies travel encrypted (`{ payload: "<ciphertext>" }`), so the envelope
 * is dropped – it has no debugging value and bloats sessions. Anything else is
 * scrubbed key-by-key.
 */
function sanitizeBody(body?: string | null) {
  if (typeof body !== "string" || body.length === 0) return body;
  if (body.length > MAX_BODY_LENGTH) {
    return `[body omitted: ${body.length} bytes]`;
  }

  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object" && "payload" in parsed) {
      return JSON.stringify({ ...parsed, payload: REDACTED });
    }
    return JSON.stringify(scrubSensitiveData(parsed));
  } catch {
    return body;
  }
}

/**
 * Initialises LogRocket once, in the browser only.
 * Safe to call from anywhere – repeated calls and SSR calls are no-ops.
 */
export function initLogRocket(): boolean {
  if (initialised) return true;
  if (typeof window === "undefined") return false; // never record on the server

  if (!isLogRocketEnabled) {
    if (process.env.NODE_ENV === "production" && !isLogRocketConfigured) {
      console.warn(
        "[LogRocket] NEXT_PUBLIC_LOGROCKET_APP_ID is not set - session replay is disabled.",
      );
    }
    return false;
  }

  try {
    LogRocket.init(APP_ID as string, {
      ...(RELEASE ? { release: RELEASE } : {}),
      console: {
        isEnabled: true,
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: (request) => ({
          ...request,
          url: sanitizeUrl(request.url),
          headers: sanitizeHeaders(request.headers),
          body: sanitizeBody(request.body),
        }),
        responseSanitizer: (response) => ({
          ...response,
          headers: sanitizeHeaders(response.headers),
          body: sanitizeBody(response.body),
        }),
      },
      browser: {
        urlSanitizer: (url) => sanitizeUrl(url),
      },
      dom: {
        // Never store what users type (passwords, OTPs, emails, card data).
        // Replay still shows that typing happened, just not the characters.
        inputSanitizer: true,
      },
      // Geo/IP data is not needed to debug sessions and is GDPR-sensitive.
      shouldCaptureIP: false,
      mergeIframes: false,
    });

    initialised = true;

    // Optional React plugin: records which React components a user clicked on
    // (searchable from the session list). It needs `document.body`, so it can
    // only be attached from the browser.
    if (typeof document !== "undefined" && document.body) {
      try {
        setupLogRocketReact();
      } catch (pluginError) {
        console.warn("[LogRocket] react plugin unavailable", pluginError);
      }
    }

    console.info(
      `[LogRocket] recording enabled${RELEASE ? ` (release ${RELEASE})` : ""}`,
    );
    return true;
  } catch (error) {
    console.warn("[LogRocket] init failed", error);
    return false;
  }
}

/** True when the SDK is loaded and recording in this session. */
export function isLogRocketActive(): boolean {
  return initialised && typeof window !== "undefined";
}

/**
 * Attaches the application user to the replay.
 * Always use the immutable DB id; name/email become searchable traits.
 */
export function identifyLogRocketUser(
  user: Pick<
    User,
    | "_id"
    | "firstName"
    | "lastName"
    | "email"
    | "isAdmin"
    | "isActive"
    | "otpEnabled"
    | "isEmailVerified"
  >,
): void {
  if (!isLogRocketActive() || !user?._id) return;

  try {
    LogRocket.identify(String(user._id), {
      name: [user.firstName, user.lastName].filter(Boolean).join(" "),
      email: user.email,
      isAdmin: Boolean(user.isAdmin),
      isActive: Boolean(user.isActive),
      otpEnabled: Boolean(user.otpEnabled),
      isEmailVerified: Boolean(user.isEmailVerified),
    });
  } catch (error) {
    console.warn("[LogRocket] identify failed", error);
  }
}

/**
 * Called when the user logs out.
 * The SDK has no "unidentify" API, so a fresh session is started to make sure
 * the next person using a shared browser is not merged into this user's replay.
 */
export function handleLogRocketLogout(): void {
  if (!isLogRocketActive()) return;

  try {
    LogRocket.track("User Logged Out");
    LogRocket.startNewSession();
  } catch (error) {
    console.warn("[LogRocket] logout handling failed", error);
  }
}

/** Manual exception reporting (API failures, Next.js error boundaries). */
export function captureLogRocketError(
  error: unknown,
  context?: Record<string, string | number | boolean>,
): void {
  if (!isLogRocketActive()) return;

  try {
    const exception =
      error instanceof Error
        ? error
        : new Error(String(error ?? "Unknown error"));
    LogRocket.captureException(
      exception,
      context ? { extra: context } : undefined,
    );
  } catch (captureError) {
    console.warn("[LogRocket] captureException failed", captureError);
  }
}

/** Manual message reporting for non-error signals worth seeing in a session. */
export function captureLogRocketMessage(message: string): void {
  if (!isLogRocketActive()) return;

  try {
    LogRocket.captureMessage(message);
  } catch (error) {
    console.warn("[LogRocket] captureMessage failed", error);
  }
}

/** Business/product events that make sessions searchable and analyzable. */
export function trackLogRocketEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined | null>,
): void {
  if (!isLogRocketActive()) return;

  try {
    LogRocket.track(eventName, properties ?? undefined);
  } catch (error) {
    console.warn("[LogRocket] track failed", error);
  }
}

/** Current session URL – attach it to support tickets or send it to the API. */
export function getLogRocketSessionURL(): string | null {
  if (!isLogRocketActive()) return null;
  return LogRocket.sessionURL ?? null;
}

/** Correlation id shared with the API (see `app/utils/apiClient.ts`). */
export function createCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Redux middleware – logs actions and state diffs to LogRocket.
 *
 * - Must be attached as the LAST middleware in the chain (LogRocket requirement).
 * - Safe to create before `initLogRocket()`; the SDK buffers until it starts.
 * - Returns a pass-through no-op during SSR or when the integration is disabled,
 *   so the store shape is identical in every environment.
 */
export function logRocketReduxMiddleware() {
  const passThrough = () => (next: any) => (action: any) => next(action);

  if (!isLogRocketEnabled || typeof window === "undefined") {
    return passThrough;
  }

  try {
    return LogRocket.reduxMiddleware({
      stateSanitizer: (state) => scrubSensitiveData(state),
      actionSanitizer: (action) => {
        // Drop noisy internal actions; scrub secrets from everything else.
        if (
          typeof action?.type === "string" &&
          /@@INIT|redux\/probeUnknownAction|persist\/PERSIST/i.test(action.type)
        ) {
          return null;
        }
        return scrubSensitiveData(action);
      },
    });
  } catch (error) {
    console.warn("[LogRocket] redux middleware unavailable", error);
    return passThrough;
  }
}

